import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { vetoReplay, vetoDone, vetoCurrentStep, vetoSideMap } from './extVetoEngine';
import MatchSeatingEditor, { seatingComplete } from './stage/MatchSeatingEditor';

// Static admin veto link of an external tournament: /ext-veto/:key (no login — the key is the credential).
// The veto is done in person; the admin enters it here for both teams (picked from the tournament's
// teams), then sets the stage seating. Everything stays in this browser tab (sessionStorage) until
// "Confirm veto & seating", which creates the match. Team A = team1 and starts the veto.

const V = {
  bg: '#0a0a0f', card: '#111118', elevated: '#1a1a24', input: '#0e0e15',
  line: 'rgba(255,255,255,0.06)', line2: 'rgba(255,255,255,0.1)',
  text: '#e8e8ed', sub: '#8b8b9e', muted: '#4a4a5a',
  blue: '#60a5fa', orange: '#fb923c', green: '#22c55e', red: '#ef4444', amber: '#f59e0b', cyan: '#22d3ee',
  mono: "var(--font-mono, 'JetBrains Mono', monospace)", display: "var(--font-display, 'Cabinet Grotesk', sans-serif)",
};
const TEAM_COLOR = { A: V.blue, B: V.orange };

const mapLabel = (name) => {
  const s = String(name || '').replace(/^(de_|cs_)/, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const newClientId = () => {
  const a = new Uint8Array(12);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
};

const card = { background: V.card, border: `1px solid ${V.line}`, borderRadius: 12, padding: '1.1rem 1.2rem' };
const label = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: V.sub, marginBottom: '0.4rem' };
const sectionLabel = { fontSize: '0.68rem', fontWeight: 700, color: V.muted, textTransform: 'uppercase', letterSpacing: '0.07em' };
function btn(color, solid) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.55rem 1rem', borderRadius: 8,
    fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer', fontFamily: 'inherit',
    background: solid ? color : `${color}14`, border: `1px solid ${solid ? color : `${color}33`}`, color: solid ? '#fff' : color,
  };
}

const selectStyles = (accent) => ({
  control: (b, s) => ({ ...b, background: V.input, borderColor: s.isFocused ? accent : V.line2, boxShadow: 'none', minHeight: 44, borderRadius: 8, ':hover': { borderColor: accent } }),
  menu: (b) => ({ ...b, background: V.elevated, border: `1px solid ${V.line2}`, zIndex: 20 }),
  menuList: (b) => ({ ...b, maxHeight: 300 }),
  option: (b, s) => ({ ...b, background: s.isFocused ? 'rgba(255,255,255,0.06)' : 'transparent', color: V.text, cursor: 'pointer', fontSize: '0.86rem' }),
  singleValue: (b) => ({ ...b, color: V.text, fontWeight: 700 }),
  input: (b) => ({ ...b, color: V.text }),
  placeholder: (b) => ({ ...b, color: V.muted }),
  indicatorSeparator: () => ({ display: 'none' }),
  noOptionsMessage: (b) => ({ ...b, color: V.sub, fontSize: '0.82rem' }),
});

function Shell({ children, cfg }) {
  const { t } = useTranslation();
  return (
    <div style={{ minHeight: 'var(--app-vh, 100vh)', background: V.bg, color: V.text, fontFamily: "var(--font-sans, 'Satoshi', sans-serif)" }}>
      <div style={{ borderBottom: `1px solid ${V.line}`, padding: '0.8rem 1.25rem', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <img src="/logo.png" alt="" style={{ width: 24, height: 24, borderRadius: 6 }} />
        <span style={{ fontWeight: 800 }}>{cfg ? cfg.tournament.name : 'Flux'}</span>
        {cfg && <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: `${V.cyan}14`, border: `1px solid ${V.cyan}30`, color: V.cyan }}>{t('extVeto.formatPill', '{{format}} veto', { format: cfg.format_label })}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: V.muted }}>{t('extVeto.adminOnly', 'Admin veto link · do not share')}</span>
      </div>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '1.5rem 1.1rem 3rem' }}>{children}</div>
    </div>
  );
}

function TeamPicker({ which, value, onChange, options, loading }) {
  const { t } = useTranslation();
  const color = TEAM_COLOR[which];
  const opt = value ? options.find(o => o.value === value.team_id) || null : null;
  return (
    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.45rem' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
        <span style={{ fontWeight: 800, color }}>{which === 'A' ? t('extVeto.teamA', 'Team A') : t('extVeto.teamB', 'Team B')}</span>
        {which === 'A' && <span style={{ fontSize: '0.7rem', color: V.muted }}>{t('extVeto.startsVeto', 'starts the veto')}</span>}
      </div>
      <Select
        isClearable
        isLoading={loading}
        value={opt}
        options={options}
        styles={selectStyles(color)}
        placeholder={t('extVeto.pickTournamentTeam', 'Search this tournament’s teams…')}
        noOptionsMessage={() => t('extVeto.noTournamentTeams', 'No team found. Teams are added on the tournament page.')}
        onChange={(o) => onChange(o ? { team_id: o.value, name: o.label } : null)}
        formatOptionLabel={(o) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{o.label}</span>
            {o.tag && <span style={{ fontFamily: V.mono, fontSize: '0.7rem', color: V.muted }}>[{o.tag}]</span>}
          </div>
        )}
      />
    </div>
  );
}

function stepText(t, step, names) {
  if (!step) return '';
  const team = names[step.t];
  if (step.a === 'ban') return t('extVeto.stepBan', '{{team}} bans', { team });
  if (step.a === 'pick') return t('extVeto.stepPick', '{{team}} picks', { team });
  if (step.a === 'side') return t('extVeto.stepSide', '{{team}} picks a side', { team });
  if (step.a === 'knife') return t('extVeto.stepDecider', 'Decider (knife for sides)');
  return step.a;
}

function Setup({ cfg, teams, teamsState, draft, setDraft, onStart }) {
  const { t } = useTranslation();
  const options = useMemo(() => teams.map(x => ({ value: x.id, label: x.name, tag: x.tag })), [teams]);
  const { teamA, teamB, bestOf } = draft;
  const same = teamA && teamB && teamA.team_id === teamB.team_id;
  const steps = cfg.formats[bestOf].steps;
  const names = { A: teamA ? teamA.name : t('extVeto.teamA', 'Team A'), B: teamB ? teamB.name : t('extVeto.teamB', 'Team B') };
  const known = (x) => x && teams.some(tm => tm.id === x.team_id);
  const canStart = cfg.ready && known(teamA) && known(teamB) && !same;
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 850 }}>{t('extVeto.newVeto', 'New veto')}</h1>
        <p style={{ margin: '0.3rem 0 0', color: V.sub, fontSize: '0.9rem' }}>{t('extVeto.setupLead', 'Choose the two teams and the format, then enter the veto as it happens. Nothing is saved until you submit.')}</p>
      </div>

      {!cfg.ready && (
        <div style={{ ...card, borderColor: `${V.amber}55`, color: V.amber, fontSize: '0.86rem' }}>
          {cfg.problem === 'not_enough_teams'
            ? t('extVeto.notEnoughTeams', 'This tournament needs at least two teams before a veto can be done. Add the teams (players and seating) on the tournament page first.')
            : t('extVeto.notEnoughMaps', 'This tournament’s owner needs 7 Active Duty maps on the Maps page before a veto can be done.')}
        </div>
      )}

      <div style={card}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <TeamPicker which="A" value={teamA} onChange={(v) => setDraft({ ...draft, teamA: v })} options={options} loading={teamsState === 'loading'} />
          <button type="button" title={t('extVeto.swap', 'Swap teams')} onClick={() => setDraft({ ...draft, teamA: teamB, teamB: teamA })}
            style={{ ...btn(V.sub), padding: '0 0.8rem', height: 44, alignSelf: 'flex-end' }}>⇄</button>
          <TeamPicker which="B" value={teamB} onChange={(v) => setDraft({ ...draft, teamB: v })} options={options} loading={teamsState === 'loading'} />
        </div>
        {teamsState === 'unavailable' && <div style={{ fontSize: '0.75rem', color: V.amber, marginTop: '0.6rem' }}>{t('extVeto.teamsUnavailable', 'The teams could not be loaded. Check your connection and reload the page.')}</div>}
        {same && <div style={{ fontSize: '0.78rem', color: V.red, marginTop: '0.6rem' }}>{t('extVeto.sameTeam', 'Team A and Team B must be different teams.')}</div>}

        <div style={{ marginTop: '1.2rem' }}>
          <span style={label}>{t('extVeto.format', 'Format')}</span>
          <div style={{ display: 'inline-flex', border: `1px solid ${V.line2}`, borderRadius: 9, overflow: 'hidden' }}>
            {[1, 3, 5].map(bo => (
              <button key={bo} type="button" onClick={() => setDraft({ ...draft, bestOf: bo })}
                style={{ padding: '0.6rem 1.4rem', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer', border: 'none', fontFamily: 'inherit',
                  background: bestOf === bo ? 'rgba(96,165,250,0.16)' : 'transparent', color: bestOf === bo ? V.blue : V.sub, borderRight: bo !== 5 ? `1px solid ${V.line2}` : 'none' }}>
                BO{bo}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '1.1rem' }}>
          <span style={label}>{t('extVeto.order', 'Veto order ({{label}})', { label: cfg.formats[bestOf].label })}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {steps.map((s, i) => {
              const c = s.t === 'System' ? V.muted : TEAM_COLOR[s.t];
              return (
                <span key={i} style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: 999, border: `1px solid ${c}40`, background: `${c}12`, color: s.t === 'System' ? V.sub : c, fontWeight: 600 }}>
                  <span style={{ fontFamily: V.mono, opacity: 0.6, marginRight: 5 }}>{i + 1}</span>{stepText(t, s, names)}
                </span>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.3rem' }}>
          <button type="button" disabled={!canStart} onClick={onStart} style={{ ...btn(V.blue, true), opacity: canStart ? 1 : 0.45, cursor: canStart ? 'pointer' : 'not-allowed', padding: '0.7rem 1.4rem' }}>
            {t('extVeto.start', 'Start veto')} →
          </button>
        </div>
      </div>
    </div>
  );
}

function MapRow({ m, mapMeta, names, step, onAct, sidePending }) {
  const { t } = useTranslation();
  const img = mapMeta && mapMeta.image_url;
  const clickable = step && (step.a === 'ban' || step.a === 'pick') && m.status === 'available';
  const actColor = step && step.a === 'ban' ? V.red : V.green;
  let badge = null;
  if (m.status === 'banned') badge = { text: t('extVeto.bannedBy', 'Banned · {{team}}', { team: names[m.by] }), color: V.red };
  if (m.status === 'picked') badge = { text: t('extVeto.pickedBy', 'Picked · {{team}}', { team: names[m.by] }), color: TEAM_COLOR[m.by] };
  if (m.status === 'decider') badge = { text: t('extVeto.decider', 'Decider'), color: V.amber };
  const sideText = m.side === 'knife' ? t('extVeto.knifeSides', 'Knife for sides')
    : m.side ? t('extVeto.startsSide', '{{team}} starts {{side}}', { team: names[m.sideBy], side: m.side.toUpperCase() }) : null;
  return (
    <div onClick={clickable ? () => onAct({ map: m.name }) : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.55rem 0.7rem', borderRadius: 9,
        background: sidePending ? 'rgba(34,211,238,0.06)' : V.card,
        border: `1px solid ${sidePending ? `${V.cyan}66` : clickable ? V.line2 : V.line}`,
        opacity: m.status === 'banned' ? 0.45 : 1, cursor: clickable ? 'pointer' : 'default', transition: 'border-color 0.12s',
      }}
      onMouseEnter={(e) => { if (clickable) e.currentTarget.style.borderColor = actColor; }}
      onMouseLeave={(e) => { if (clickable) e.currentTarget.style.borderColor = V.line2; }}>
      <div style={{ width: 72, height: 42, borderRadius: 6, overflow: 'hidden', background: V.elevated, flexShrink: 0 }}>
        {img && <img src={img} alt="" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: m.status === 'banned' ? 'grayscale(1)' : 'none' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, textDecoration: m.status === 'banned' ? 'line-through' : 'none' }}>{mapLabel(m.name)}</div>
        {sideText && <div style={{ fontSize: '0.74rem', color: m.side === 'knife' ? V.sub : TEAM_COLOR[m.sideBy], marginTop: 1 }}>{sideText}</div>}
      </div>
      {badge && <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: `${badge.color}14`, border: `1px solid ${badge.color}33`, color: badge.color, whiteSpace: 'nowrap' }}>{badge.text}</span>}
      {clickable && <span style={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.05em', color: actColor, padding: '0.2rem 0.4rem' }}>{step.a === 'ban' ? t('extVeto.ban', 'BAN') : t('extVeto.pick', 'PICK')}</span>}
    </div>
  );
}

/** Veto log as plain text from the in-browser veto state (works mid-veto; adds the maps once done). */
function boardLogText(t, names, bestOf, state, done) {
  const lines = [`${names.A} vs ${names.B} · BO${bestOf}`, ''];
  state.log.forEach((l, i) => {
    const who = l.team ? names[l.team] : t('extVeto.decider', 'Decider');
    const map = mapLabel(l.map);
    if (l.action === 'ban') lines.push(`${i + 1}. ${who} ${t('extVeto.logBan', 'banned {{map}}', { map })}`);
    else if (l.action === 'pick') lines.push(`${i + 1}. ${who} ${t('extVeto.logPick', 'picked {{map}}', { map })}`);
    else if (l.action === 'side') lines.push(`${i + 1}. ${who} ${t('extVeto.logSide', 'starts {{side}} on {{map}}', { side: String(l.side).toUpperCase(), map })}`);
    else if (l.action === 'decider') lines.push(`${i + 1}. ${t('extVeto.decider', 'Decider')}: ${l.side === 'knife' ? t('extVeto.logDeciderKnife', '{{map}} (knife for sides)', { map }) : map}`);
  });
  if (done) {
    lines.push('');
    state.played.forEach((name, i) => {
      const m = state.maps.find(x => x.name === name);
      const pick = m.status === 'decider' ? t('extVeto.decider', 'Decider') : t('extVeto.pickOf', '{{team}}’s pick', { team: names[m.by] });
      const side = m.side === 'knife' || !m.side ? t('extVeto.knifeSides', 'Knife for sides') : t('extVeto.startsSide', '{{team}} starts {{side}}', { team: names[m.sideBy], side: m.side.toUpperCase() });
      lines.push(`${t('extVeto.mapN', 'Map {{n}}', { n: i + 1 })}: ${mapLabel(name)} (${pick}) · ${side}`);
    });
  }
  return lines.join('\n');
}

function Board({ cfg, draft, inputs, setInputs, onBack, onContinue, submitting = false }) {
  const { t } = useTranslation();
  const [confirmReset, setConfirmReset] = useState(false);
  const [logCopied, setLogCopied] = useState(false);
  const steps = cfg.formats[draft.bestOf].steps;
  const mapNames = useMemo(() => cfg.maps.map(m => m.name), [cfg.maps]);
  const replay = useMemo(() => vetoReplay(steps, mapNames, inputs), [steps, mapNames, inputs]);
  const state = replay.state || vetoReplay(steps, mapNames, []).state;
  const done = vetoDone(steps, state);
  const step = vetoCurrentStep(steps, state);
  const sideMap = vetoSideMap(steps, state);
  const names = { A: draft.teamA.name, B: draft.teamB.name };
  const act = (input) => setInputs([...inputs, input]);
  const undo = useCallback(() => setInputs(inputs.slice(0, -1)), [inputs, setInputs]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && inputs.length && !submitting) { e.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inputs.length, submitting, undo]);

  const meta = Object.fromEntries(cfg.maps.map(m => [m.name, m]));
  const stepNo = Math.min(inputs.length + 1, steps.filter(s => s.t !== 'System').length);
  const totalInputs = steps.filter(s => s.t !== 'System').length;
  const tc = step ? TEAM_COLOR[step.t] : V.green;

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* match header */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 850, color: V.blue, fontSize: '1.05rem' }}>{names.A}</span>
        <span style={{ color: V.muted, fontWeight: 700 }}>vs</span>
        <span style={{ fontWeight: 850, color: V.orange, fontSize: '1.05rem' }}>{names.B}</span>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: 'rgba(255,255,255,0.05)', border: `1px solid ${V.line2}`, color: V.sub }}>{cfg.formats[draft.bestOf].label}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btn(V.sub)} disabled={submitting} onClick={onBack}>← {t('extVeto.changeSetup', 'Teams & format')}</button>
          <button type="button" style={{ ...btn(V.amber), opacity: inputs.length ? 1 : 0.4 }} disabled={!inputs.length || submitting} onClick={undo} title="Ctrl+Z">↶ {t('extVeto.undo', 'Undo')}</button>
          {!confirmReset
            ? <button type="button" style={{ ...btn(V.red), opacity: inputs.length ? 1 : 0.4 }} disabled={!inputs.length || submitting} onClick={() => setConfirmReset(true)}>⟲ {t('extVeto.reset', 'Reset')}</button>
            : (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: V.red }}>{t('extVeto.resetConfirm', 'Clear the whole veto?')}</span>
                <button type="button" style={btn(V.red, true)} onClick={() => { setInputs([]); setConfirmReset(false); }}>{t('extVeto.resetYes', 'Reset')}</button>
                <button type="button" style={btn(V.sub)} onClick={() => setConfirmReset(false)}>{t('common.cancel', 'Cancel')}</button>
              </span>
            )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '2 1 440px', minWidth: 0, display: 'grid', gap: '0.8rem' }}>
          {/* whose turn */}
          {!done && step && (
            <div style={{ ...card, borderColor: `${tc}55`, padding: '0.9rem 1.1rem' }}>
              <div style={sectionLabel}>{t('extVeto.stepOf', 'Step {{n}} of {{total}}', { n: stepNo, total: totalInputs })}</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 850, marginTop: 4 }}>
                <span style={{ color: tc }}>{names[step.t]}</span>{' '}
                {step.a === 'ban' && t('extVeto.turnBan', 'bans a map')}
                {step.a === 'pick' && t('extVeto.turnPick', 'picks a map')}
                {step.a === 'side' && t('extVeto.turnSide', 'chooses their starting side on {{map}}', { map: mapLabel(sideMap) })}
              </div>
              {step.a === 'side' && (
                <div style={{ display: 'flex', gap: 10, marginTop: '0.8rem', flexWrap: 'wrap' }}>
                  {[['ct', 'CT', '#60a5fa', '/veto_ct.png'], ['t', 'T', '#f87171', '/veto_t.png']].map(([side, txt, color, icon]) => (
                    <button key={side} type="button" onClick={() => act({ side })}
                      style={{ flex: '1 1 140px', position: 'relative', overflow: 'hidden', padding: '1rem', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                        background: `${color}12`, border: `1px solid ${color}40`, color, textAlign: 'left' }}>
                      <img src={icon} alt="" style={{ position: 'absolute', right: -8, bottom: -24, height: 96, opacity: 0.15, pointerEvents: 'none' }} />
                      <div style={{ fontSize: '1.4rem', fontWeight: 900 }}>{txt}</div>
                      <div style={{ fontSize: '0.72rem', color: V.sub }}>{side === 'ct' ? t('extVeto.ctLong', 'Counter-Terrorist') : t('extVeto.tLong', 'Terrorist')}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {done && (
            <div style={{ ...card, borderColor: `${V.green}55` }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 850, color: V.green }}>✓ {t('extVeto.complete', 'Veto complete')}</div>
              <p style={{ margin: '0.3rem 0 0.9rem', color: V.sub, fontSize: '0.86rem' }}>{t('extVeto.checkThenSeating', 'Check the maps and sides below, then set the stage seating. Nothing is created until you confirm veto and seating; you can still undo until then.')}</p>
              <div style={{ display: 'grid', gap: 6 }}>
                {state.played.map((name, i) => {
                  const m = state.maps.find(x => x.name === name);
                  return (
                    <div key={name} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: V.mono, color: V.muted, width: 52 }}>{t('extVeto.mapN', 'Map {{n}}', { n: i + 1 })}</span>
                      <span style={{ fontWeight: 800, minWidth: 90 }}>{mapLabel(name)}</span>
                      <span style={{ color: m.status === 'decider' ? V.amber : TEAM_COLOR[m.by], fontSize: '0.8rem' }}>{m.status === 'decider' ? t('extVeto.decider', 'Decider') : t('extVeto.pickOf', '{{team}}’s pick', { team: names[m.by] })}</span>
                      <span style={{ color: V.sub, fontSize: '0.8rem', marginLeft: 'auto' }}>
                        {m.side === 'knife' || !m.side ? t('extVeto.knifeSides', 'Knife for sides') : t('extVeto.startsSide', '{{team}} starts {{side}}', { team: names[m.sideBy], side: m.side.toUpperCase() })}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" onClick={onContinue} style={{ ...btn(V.blue, true), padding: '0.7rem 1.4rem' }}>
                  {t('extVeto.continueSeating', 'Continue to stage seating')} →
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gap: 6 }}>
            {state.maps.map(m => (
              <MapRow key={m.name} m={m} mapMeta={meta[m.name]} names={names} step={done ? null : step} onAct={act} sidePending={!done && step && step.a === 'side' && sideMap === m.name} />
            ))}
          </div>
        </div>

        {/* log */}
        <div style={{ ...card, flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.6rem' }}>
            <div style={sectionLabel}>{t('extVeto.log', 'Veto log')}</div>
            {state.log.length > 0 && (
              <button type="button" style={{ ...btn(logCopied ? V.green : V.cyan), marginLeft: 'auto', padding: '0.3rem 0.65rem', fontSize: '0.74rem' }}
                onClick={async () => { if (await copyText(boardLogText(t, names, draft.bestOf, state, done))) { setLogCopied(true); setTimeout(() => setLogCopied(false), 1800); } }}>
                {logCopied ? `✓ ${t('ext.copied', 'Copied')}` : t('ext.copy', 'Copy')}
              </button>
            )}
          </div>
          {!state.log.length && <div style={{ fontSize: '0.82rem', color: V.muted }}>{t('extVeto.logEmpty', 'Nothing yet. {{team}} starts.', { team: names.A })}</div>}
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7 }}>
            {state.log.map((l, i) => (
              <li key={i} style={{ display: 'flex', gap: 8, fontSize: '0.82rem', alignItems: 'baseline' }}>
                <span style={{ fontFamily: V.mono, color: V.muted, fontSize: '0.72rem', width: 18 }}>{i + 1}</span>
                <span style={{ color: l.team ? TEAM_COLOR[l.team] : V.amber, fontWeight: 700 }}>{l.team ? names[l.team] : t('extVeto.decider', 'Decider')}</span>
                <span style={{ color: V.sub }}>
                  {l.action === 'ban' && t('extVeto.logBan', 'banned {{map}}', { map: mapLabel(l.map) })}
                  {l.action === 'pick' && t('extVeto.logPick', 'picked {{map}}', { map: mapLabel(l.map) })}
                  {l.action === 'side' && t('extVeto.logSide', 'starts {{side}} on {{map}}', { side: l.side.toUpperCase(), map: mapLabel(l.map) })}
                  {l.action === 'decider' && (l.side === 'knife' ? t('extVeto.logDeciderKnife', '{{map}} (knife for sides)', { map: mapLabel(l.map) }) : mapLabel(l.map))}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

/** The two veto teams as the seating editor wants them (players + default seating). */
function seatingTeams(teams, draft) {
  const byId = (id) => teams.find(x => x.id === id);
  const shape = (tm) => ({ name: tm.name, players: tm.players.map(p => ({ steam_id: p.steam_id, ign: p.ign })), defaults: tm.seating.seats.map(s => s.steam_id) });
  const a = draft.teamA && byId(draft.teamA.team_id), b = draft.teamB && byId(draft.teamB.team_id);
  return a && b ? { team1: shape(a), team2: shape(b) } : null;
}
const seatingFor = (draft) => (draft.teamA && draft.teamB ? `${draft.teamA.team_id}:${draft.teamB.team_id}` : '');
function defaultSeating(sTeams, draft) {
  return { for: seatingFor(draft), team1_stage_side: 'left', team1: { seats: sTeams.team1.defaults }, team2: { seats: sTeams.team2.defaults } };
}

function SeatingStep({ cfg, draft, inputs, sTeams, seating, setSeating, onBack, onSubmit, submitting, submitError }) {
  const { t } = useTranslation();
  const steps = cfg.formats[draft.bestOf].steps;
  const state = vetoReplay(steps, cfg.maps.map(m => m.name), inputs).state;
  const ok = seatingComplete(seating);
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 850, color: V.blue, fontSize: '1.05rem' }}>{draft.teamA.name}</span>
        <span style={{ color: V.muted, fontWeight: 700 }}>vs</span>
        <span style={{ fontWeight: 850, color: V.orange, fontSize: '1.05rem' }}>{draft.teamB.name}</span>
        <span style={{ fontSize: '0.8rem', color: V.sub }}>{state && state.played.map(mapLabel).join(' → ')}</span>
        <button type="button" style={{ ...btn(V.sub), marginLeft: 'auto' }} disabled={submitting} onClick={onBack}>← {t('extVeto.backToVeto', 'Back to the veto')}</button>
      </div>
      <div style={card}>
        <div style={sectionLabel}>{t('extVeto.seatingStep', 'Stage seating')}</div>
        <p style={{ margin: '0.3rem 0 0.9rem', color: V.sub, fontSize: '0.86rem' }}>
          {t('extVeto.seatingLead', 'Seen from the crowd. Choose which team sits on which side; each side starts from the team’s saved seating. Swap in a substitute for this match if needed - the teams’ saved seating is not changed.')}
        </p>
        <MatchSeatingEditor teams={sTeams} value={seating} onChange={(v) => setSeating({ ...v, for: seating.for })} />
        {submitError && <div style={{ marginTop: '0.8rem', fontSize: '0.82rem', color: V.red }}>{submitError}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" onClick={onSubmit} disabled={submitting || !ok} style={{ ...btn(V.green, true), padding: '0.7rem 1.4rem', opacity: submitting || !ok ? 0.55 : 1, cursor: ok ? 'pointer' : 'not-allowed' }}>
            {submitting ? t('extVeto.submitting', 'Creating match…') : t('extVeto.confirmVetoSeating', 'Confirm veto & seating arrangement')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Plain-text veto log (for Discord / chat): every action in order, then the maps with their sides. */
function vetoLogText(t, result) {
  const v = result.veto;
  const sideTxt = (s) => (s || '').toUpperCase();
  const lines = [`${result.team1} vs ${result.team2} · BO${result.best_of}`, ''];
  for (const a of v.actions) {
    const who = a.team_name || t('extVeto.decider', 'Decider');
    const map = mapLabel(a.map);
    if (a.action === 'ban') lines.push(`${a.order}. ${who} ${t('extVeto.logBan', 'banned {{map}}', { map })}`);
    else if (a.action === 'pick') lines.push(`${a.order}. ${who} ${t('extVeto.logPick', 'picked {{map}}', { map })}`);
    else if (a.action === 'side') lines.push(`${a.order}. ${who} ${t('extVeto.logSide', 'starts {{side}} on {{map}}', { side: sideTxt(a.side), map })}`);
    else if (a.action === 'decider') lines.push(`${a.order}. ${t('extVeto.decider', 'Decider')}: ${map}`);
  }
  lines.push('');
  for (const m of v.maps) {
    const pick = m.picked_by === 'decider' ? t('extVeto.decider', 'Decider') : t('extVeto.pickOf', '{{team}}’s pick', { team: m.picked_by_name });
    const sides = m.knife_for_sides || !m.starting_sides
      ? t('extVeto.knifeSides', 'Knife for sides')
      : `${result.team1} ${sideTxt(m.starting_sides.team1)} · ${result.team2} ${sideTxt(m.starting_sides.team2)}`;
    lines.push(`${t('extVeto.mapN', 'Map {{n}}', { n: m.order })}: ${mapLabel(m.map)} (${pick}) · ${sides}`);
  }
  return lines.join('\n');
}

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (_) {
    const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
    let ok = false; try { ok = document.execCommand('copy'); } catch (__) { /* ignore */ }
    document.body.removeChild(ta);
    return ok;
  }
}

function Done({ result, onNew }) {
  const { t } = useTranslation();
  const v = result.veto;
  const [copied, setCopied] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const log = useMemo(() => vetoLogText(t, result), [t, result]);
  const copy = async () => { if (await copyText(log)) { setCopied(true); setTimeout(() => setCopied(false), 1800); } };
  const tid = result.tournament_id;
  return (
    <div style={{ ...card, maxWidth: 620, margin: '2rem auto', textAlign: 'center', padding: '2rem 1.5rem' }}>
      <div style={{ fontSize: '2rem' }}>✓</div>
      <h1 style={{ margin: '0.3rem 0', fontSize: '1.35rem', fontWeight: 850 }}>{t('extVeto.created', 'Match created')}</h1>
      <div style={{ fontSize: '1.05rem', fontWeight: 800, marginTop: '0.4rem' }}>
        <span style={{ color: V.blue }}>{result.team1}</span> <span style={{ color: V.muted }}>vs</span> <span style={{ color: V.orange }}>{result.team2}</span>
        <span style={{ color: V.sub, fontWeight: 600 }}> · BO{result.best_of}</span>
      </div>
      <div style={{ color: V.sub, fontSize: '0.88rem', margin: '0.6rem 0 1.2rem' }}>{v.maps.map(m => mapLabel(m.map)).join(' → ')}</div>
      <p style={{ color: V.sub, fontSize: '0.84rem', margin: '0 0 1.3rem' }}>
        {result.duplicate ? t('extVeto.alreadyCreated', 'This veto was already submitted; nothing new was created.') : t('extVeto.createdHint', 'Start the servers with the tournament’s log address. The match picks them up automatically.')}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button type="button" style={btn(copied ? V.green : V.cyan)} onClick={copy}>{copied ? `✓ ${t('extVeto.logCopied', 'Veto log copied')}` : t('extVeto.copyLog', 'Copy veto log')}</button>
        <button type="button" style={btn(V.sub)} onClick={() => setShowLog(!showLog)}>{showLog ? t('extVeto.hideLog', 'Hide log') : t('extVeto.showLog', 'Show log')}</button>
      </div>
      {showLog && (
        <pre style={{ textAlign: 'left', background: V.input, border: `1px solid ${V.line}`, borderRadius: 8, padding: '0.7rem 0.85rem', color: V.sub, fontFamily: V.mono, fontSize: '0.76rem', whiteSpace: 'pre-wrap', margin: '0 0 1.2rem' }}>{log}</pre>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        {tid && <Link to={`/admin/tournaments/${tid}/matches/${result.id}`} style={{ ...btn(V.green, true), padding: '0.7rem 1.2rem', textDecoration: 'none' }}>{t('extVeto.goToMatch', 'Go to the match')}</Link>}
        {tid && <Link to={`/admin/tournaments/${tid}`} style={{ ...btn(V.blue), padding: '0.7rem 1.2rem', textDecoration: 'none' }}>{t('extVeto.goToTournament', 'Go to the tournament')}</Link>}
        <button type="button" style={{ ...btn(V.sub), padding: '0.7rem 1.2rem' }} onClick={onNew}>{t('extVeto.another', 'Start another veto')}</button>
      </div>
    </div>
  );
}

export default function ExtVeto() {
  const { t } = useTranslation();
  const { key } = useParams();
  // v2: team ids are per-tournament team numbers — a draft saved before that change (old global
  // team ids) must not be resumed, so it lives under the old key and is simply ignored.
  const storeKey = `extveto:v2:${key}`;
  const [cfg, setCfg] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsState, setTeamsState] = useState('loading');
  const saved = useMemo(() => { try { return JSON.parse(sessionStorage.getItem(storeKey) || 'null'); } catch (_) { return null; } }, [storeKey]);
  // Drafts from before tournament teams (VRS / custom names) have no team_id → start clean.
  const savedOk = saved && saved.draft && [saved.draft.teamA, saved.draft.teamB].every(x => !x || x.team_id);
  const [phase, setPhase] = useState(savedOk && ['veto', 'seating'].includes(saved.phase) ? saved.phase : 'setup');
  const [draft, setDraft] = useState(savedOk ? saved.draft : { teamA: null, teamB: null, bestOf: 3 });
  const [inputs, setInputs] = useState(savedOk && Array.isArray(saved.inputs) ? saved.inputs : []);
  const [seating, setSeating] = useState(savedOk && saved.seating ? saved.seating : null);
  const [clientId, setClientId] = useState(saved && saved.clientId ? saved.clientId : newClientId());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = 'Veto · Flux';
    axios.get(`/api/ext-veto/${key}`).then(r => setCfg(r.data)).catch(e => setLoadError((e.response && e.response.data && e.response.data.error && e.response.data.error.message) || t('extVeto.loadFailed', 'Could not load this veto link.')));
    axios.get(`/api/ext-veto/${key}/teams`).then(r => { setTeams(r.data.teams || []); setTeamsState(r.data.unavailable ? 'unavailable' : 'ok'); }).catch(() => setTeamsState('unavailable'));
  }, [key, t]);

  // Everything stays in this tab until submit (sessionStorage: survives a reload, separate per tab).
  useEffect(() => {
    if (result) { sessionStorage.removeItem(storeKey); return; }
    sessionStorage.setItem(storeKey, JSON.stringify({ phase, draft, inputs, clientId, seating }));
  }, [phase, draft, inputs, clientId, seating, result, storeKey]);

  const startNew = () => { setResult(null); setInputs([]); setSeating(null); setDraft({ teamA: null, teamB: null, bestOf: draft.bestOf }); setClientId(newClientId()); setPhase('setup'); setSubmitError(''); };

  const sTeams = useMemo(() => seatingTeams(teams, draft), [teams, draft]);
  // Seating step: start from the teams' saved seating (kept if the same two teams come back to it).
  const toSeating = () => {
    if (!sTeams) return;
    if (!seating || seating.for !== seatingFor(draft)) setSeating(defaultSeating(sTeams, draft));
    setSubmitError('');
    setPhase('seating');
  };

  const submit = async () => {
    setSubmitting(true); setSubmitError('');
    try {
      const r = await axios.post(`/api/ext-veto/${key}/submit`, {
        client_id: clientId, team_a: { team_id: draft.teamA.team_id }, team_b: { team_id: draft.teamB.team_id }, best_of: draft.bestOf,
        maps: cfg.maps.map(m => m.name), actions: inputs,
        seating: { team_a_stage_side: seating.team1_stage_side, team_a: seating.team1, team_b: seating.team2 },
      });
      setResult({ ...r.data.match, duplicate: r.data.duplicate });
    } catch (e) {
      const er = e.response && e.response.data && e.response.data.error;
      setSubmitError(er ? er.message : t('extVeto.submitFailed', 'Could not submit. Check your connection and try again; the veto is kept.'));
    } finally { setSubmitting(false); }
  };

  if (loadError) return <Shell><div style={{ ...card, maxWidth: 520, margin: '3rem auto', textAlign: 'center', color: V.sub }}>{loadError}</div></Shell>;
  if (!cfg) return <Shell><div style={{ color: V.sub, textAlign: 'center', marginTop: '3rem' }}>{t('common.loading', 'Loading...')}</div></Shell>;

  return (
    <Shell cfg={cfg}>
      {result ? <Done result={result} onNew={startNew} />
        : phase === 'setup'
          ? <Setup cfg={cfg} teams={teams} teamsState={teamsState} draft={draft}
              setDraft={(d) => { if (d.bestOf !== draft.bestOf) setInputs([]); setDraft(d); }}
              onStart={() => setPhase('veto')} />
          : phase === 'seating' && sTeams && seating
            ? <SeatingStep cfg={cfg} draft={draft} inputs={inputs} sTeams={sTeams} seating={seating} setSeating={setSeating}
                onBack={() => setPhase('veto')} onSubmit={submit} submitting={submitting} submitError={submitError} />
            : phase === 'seating' && teamsState === 'loading'
              ? <div style={{ color: V.sub, textAlign: 'center', marginTop: '3rem' }}>{t('common.loading', 'Loading...')}</div>
              : <Board cfg={cfg} draft={draft} inputs={inputs} setInputs={(x) => { setInputs(x); setSubmitError(''); }}
                  onBack={() => setPhase('setup')} onContinue={toSeating} />}
    </Shell>
  );
}
